/**
 * Category margins and the default markup — the sell price a quotation line starts at before
 * anyone touches it.
 *
 * Same document as the pricing rules (`settings.save_pricing_rules` writes rules, margins and
 * default markup together), so this card stages a draft and saves once, for the same reason: a
 * half-typed "1" on the way to "15" must never become the margin the quotation applies.
 *
 * Live values on 2026-08-11: Provision 15, Marine 18, Engine 20, Safety 18, default markup 15.
 */
import { useEffect, useState } from 'react'
import { Card } from '../../components/ui'
import { usePricingRules, useSavePricingRules } from '../../lib/api/useSettings'
import { ITEM_CATEGORIES, type CategoryMargins, type PricingRulesSettings } from '../../lib/types'

export function CategoryMarginsCard() {
  const { data, isLoading, error } = usePricingRules()
  const [draft, setDraft] = useState<PricingRulesSettings | null>(null)
  const [saved, setSaved] = useState(false)

  const save = useSavePricingRules({
    onSuccess: (next) => {
      setDraft(next)
      setSaved(true)
    },
  })

  useEffect(() => {
    if (data && !draft) setDraft(data)
  }, [data, draft])

  if (isLoading) {
    return (
      <Card style={{ marginBottom: 14 }}>
        <b>📊 Category margins</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 8 }}>Loading…</div>
      </Card>
    )
  }

  if (error || !draft) {
    return (
      <Card style={{ marginBottom: 14 }}>
        <b>📊 Category margins</b>
        <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>
          {error instanceof Error ? error.message : 'Could not load margins.'}
        </div>
      </Card>
    )
  }

  const margins = draft.category_margins ?? ({} as CategoryMargins)
  const dirty = JSON.stringify(draft) !== JSON.stringify(data)

  const patch = (next: Partial<PricingRulesSettings>) => {
    setSaved(false)
    setDraft((d) => (d ? { ...d, ...next } : d))
  }

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <b>📊 Category margins</b>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
            The markup a quotation line starts at, per item category. A price typed on the line
            overrides it — this only sets where the purchaser begins.
          </div>
          {/*
            SAID OUT LOUD, because the alternative is four inputs that accept a number and change
            nothing.

            `marginFor` looks a margin up by `Item.category`, and this app only recognises the four
            below. The live catalogue does not use them: measured across 200 items on 2026-09-04
            the `category` field holds item-GROUP names — `01.FROZEN BEEF` (47), `03.FROZEN FISH`
            (81), `02.FROZEN CHICKEN AND DUCK` (64), `03.SNACKS`, `test10`, `Miscellenious` — and
            not one is `Provision`, `Marine`, `Engine` or `Safety`. `toItem`'s guard therefore
            leaves every category unset, and every line falls through to the default markup. The
            comparison payload carries no category at all.

            So these four are saved and honoured by the code, and simply never match anything on
            this data. Filed as `D23`. Until an item actually carries one of the four, the ONLY
            figure that reaches a quotation is Default markup.
          */}
          {/*
            RETIRED 2026-09-04, at Akhil's instruction: "remove the price markup and default markup
            from settings, or disable it as of now — this screen basically updates the markup for
            each line item", meaning the commission panel on the quotation.

            Disabled rather than deleted. The values are real fields the server still stores, and a
            card that vanishes takes with it any explanation of where the setting went; a purchaser
            who remembers setting 15% here deserves to be told what now decides it.

            The four category margins were already inert on this data (`D23`) — no item carries one
            of the four categories — so this loses nothing that was working.
          */}
          <div
            style={{
              fontSize: 12,
              color: 'var(--warn)',
              background: 'var(--warnbg)',
              border: '1px solid var(--warn)',
              borderRadius: 8,
              padding: '7px 10px',
              margin: '0 0 10px',
            }}
          >
            ⚠ <b>These no longer price anything.</b> The markup is now set per quotation, on the{' '}
            <b>Quotation commission</b> panel of the customer quotation, and applied to each line
            from there. These fields are kept read-only because the server still stores them.
            {' '}The four category margins were already matching nothing — no item in the catalogue
            carries one of these categories (<code>D23</code>).
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {saved && !dirty && <span style={{ color: 'var(--ok)', fontSize: 12 }}>Saved</span>}
          {dirty && (
            <button type="button" className="btn sm" onClick={() => { setDraft(data ?? null); setSaved(false) }}>
              Discard
            </button>
          )}
          <button
            type="button"
            className="btn sm pri"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {save.error && (
        <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{save.error.message}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {ITEM_CATEGORIES.map((cat) => (
          <label className="ef" style={{ width: 150 }} key={cat}>
            <span>{cat} margin %</span>
            <input
              type="number"
              disabled
              value={margins[cat] ?? ''}
              placeholder={String(draft.default_markup_pct ?? '')}
              onChange={(e) =>
                patch({
                  category_margins: {
                    ...(draft.category_margins ?? ({} as CategoryMargins)),
                    [cat]: Number(e.target.value),
                  } as CategoryMargins,
                })
              }
            />
          </label>
        ))}

        <label className="ef" style={{ width: 170 }}>
          <span>Default markup %</span>
          <input
            type="number"
            disabled
            value={draft.default_markup_pct ?? ''}
            onChange={(e) => patch({ default_markup_pct: Number(e.target.value) })}
          />
        </label>
      </div>

      <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
        Read-only — the markup now lives on each quotation. Base currency is{' '}
        <b>{draft.base_currency}</b>, which comes from the company and is not editable here.
      </div>
    </Card>
  )
}
